import { createContext, useCallback, useContext } from 'react'
import { type UserPreferences, usePreferences } from '../../data/userProfile.ts'
import { type ConfigType, dayjs } from '../../dayjs.ts'

// Overrides the user's timezone for every date formatted below it
export const DisplayTimezone = createContext<string | undefined>(undefined)

export const useFormatDate = () => {
	const { data: userPreferences } = usePreferences()
	const displayTimezone = useContext(DisplayTimezone)
	const { dateFormat = 'DAY_MONTH_YEAR', timezone: userTimezone = 'UTC', language = 'EN' } = userPreferences ?? {}
	const defaultTimezone = displayTimezone ?? userTimezone

	const formatFn = useCallback(
		(date: RequiredDate, format: DateTimeFormat, timezone: UserPreferences['timezone'] = defaultTimezone) =>
			formatDate(date, format, dateFormat, timezone, language),
		[dateFormat, defaultTimezone, language],
	)
	return formatFn
}

export const useDateFormat = () => {
	const { data: userPreferences } = usePreferences()
	const { dateFormat = 'DAY_MONTH_YEAR' } = userPreferences ?? {}

	return (outputFormat: DateTimeFormat) => {
		return formatMap[dateFormat][outputFormat]
	}
}

const formatDate = (
	date: RequiredDate,
	outputFormat: DateTimeFormat,
	dateFormat: UserPreferences['dateFormat'],
	timezone: string,
	language: string,
) => {
	try {
		const formatToUse = formatMap[dateFormat][outputFormat]
		return dayjs(date).tz(timezone).locale(language.toLowerCase()).format(formatToUse)
	} catch (e) {
		console.error('Error formatting date:', e)
		return 'Invalid Date'
	}
}

type RequiredDate = Exclude<ConfigType, undefined | null>

const _dateTimeFormats = [
	'ddd.D.MMM',
	'ddd.DD.MM',
	'DD.MM.YYYY',
	'DD.MMM.YYYY',
	'D.MMM',
	'DD.MM',
	'HH.mm',
	'HH.mm.ss',
	'HH.mm.ss.SSS',
	'DD.MM.YYYY.HH.mm',
	'DD.MMM.HH.mm',
	'DD.MM.YYYY.HH.mm.ss',
	'DD.MM.YYYY.HH.mm.ss.SSS',
	'DD.MM.HH.mm',
	'Z',
] as const
export type DateTimeFormat = (typeof _dateTimeFormats)[number]

const formatMap = {
	DAY_MONTH_YEAR: {
		'ddd.D.MMM': 'ddd D MMM',
		'ddd.DD.MM': 'ddd DD.MM',
		'DD.MM.YYYY': 'DD.MM.YYYY',
		'DD.MMM.YYYY': 'D MMM YYYY',
		'D.MMM': 'D MMM',
		'DD.MM': 'DD.MM',
		'HH.mm': 'HH:mm',
		'HH.mm.ss': 'HH:mm:ss',
		'HH.mm.ss.SSS': 'HH:mm:ss.SSS',
		'DD.MM.HH.mm': 'DD.MM HH:mm',
		'DD.MM.YYYY.HH.mm': 'DD.MM.YYYY HH:mm',
		'DD.MM.YYYY.HH.mm.ss': 'DD.MM.YYYY HH:mm:ss',
		'DD.MM.YYYY.HH.mm.ss.SSS': 'DD.MM.YYYY HH:mm:ss.SSS',
		'DD.MMM.HH.mm': 'DD MMM HH:mm',
		Z: 'Z',
	},
	MONTH_DAY_YEAR: {
		'ddd.D.MMM': 'ddd MMM D',
		'ddd.DD.MM': 'ddd MM.DD',
		'DD.MM.YYYY': 'MM.DD.YYYY',
		'DD.MMM.YYYY': 'MMM D YYYY',
		'D.MMM': 'MMM D',
		'DD.MM': 'MM.DD',
		'HH.mm': 'HH:mm',
		'HH.mm.ss': 'HH:mm:ss',
		'HH.mm.ss.SSS': 'HH:mm:ss.SSS',
		'DD.MM.HH.mm': 'MM.DD HH:mm',
		'DD.MM.YYYY.HH.mm': 'MM.DD.YYYY HH:mm',
		'DD.MM.YYYY.HH.mm.ss': 'MM.DD.YYYY HH:mm:ss',
		'DD.MM.YYYY.HH.mm.ss.SSS': 'MM.DD.YYYY HH:mm:ss.SSS',
		'DD.MMM.HH.mm': 'MMM DD HH:mm',
		Z: 'Z',
	},
} as const satisfies Record<UserPreferences['dateFormat'], Record<DateTimeFormat, string>>
